import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// If the session expires (or a request slips through without the cookie),
// force a re-check by reloading — AuthGate will render the password screen.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    if (
      err?.response?.status === 401 &&
      !url.includes("/auth/unlock") &&
      !url.includes("/auth/status")
    ) {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

export const fetchAuthStatus = () =>
  api.get("/auth/status").then((r) => r.data);
export const unlockSite = (password) =>
  api.post("/auth/unlock", { password }).then((r) => r.data);
export const lockSite = () => api.post("/auth/lock").then((r) => r.data);

export const fetchConvergence = () => api.get("/convergence").then((r) => r.data);
export const fetchExperts = () => api.get("/experts").then((r) => r.data);
export const fetchExpertMentions = (id) =>
  api.get(`/experts/${id}/mentions`).then((r) => r.data);
export const fetchTickerMentions = (ticker) =>
  api.get(`/tickers/${ticker}/mentions`).then((r) => r.data);

// ---- Public (landing page waitlist) ----
// Uses a bare axios call rather than the shared `api` instance so the 401
// reload interceptor doesn't fire — the landing page is intentionally public
// and any accidental gate response should stay silent.
export const joinWaitlist = (email, sourceNote, attribution) =>
  axios
    .post(
      `${API}/waitlist`,
      {
        email,
        source_note: sourceNote || null,
        ...(attribution || {}),
      },
      { withCredentials: true }
    )
    .then((r) => r.data);

// ---- Admin (M5 Review Queue) ----
// A dedicated axios instance so its 401 handling doesn't reload the whole
// site; the AdminLogin page needs to render 401s as an inline error.
export const adminApi = axios.create({ baseURL: API, withCredentials: true });

export const fetchAdminAuthStatus = () =>
  adminApi.get("/admin/auth/status").then((r) => r.data);
export const adminLogin = (token) =>
  adminApi.post("/admin/auth/login", { token }).then((r) => r.data);
export const adminLogout = () =>
  adminApi.post("/admin/auth/logout").then((r) => r.data);
export const fetchReviewMentions = () =>
  adminApi.get("/admin/mentions/review").then((r) => r.data);
export const approveMention = (id) =>
  adminApi.post(`/admin/mentions/${id}/approve`).then((r) => r.data);
export const rejectMention = (id, reason) =>
  adminApi
    .post(`/admin/mentions/${id}/reject`, reason ? { reason } : {})
    .then((r) => r.data);
export const fetchAdminWaitlist = () =>
  adminApi.get("/admin/waitlist").then((r) => r.data);
export const ADMIN_WAITLIST_CSV_URL = `${API}/admin/waitlist/export.csv`;

// ---- Billing (M2) ----
// All authed calls run through a plain axios instance with withCredentials
// so the user-auth cookies flow. We don't reuse the site-password `api`
// instance because these paths must work for logged-in-but-unpaid users too.
const billingApi = axios.create({ baseURL: API, withCredentials: true });

export const fetchPlans = () =>
  billingApi.get("/billing/plans").then((r) => r.data);
export const startCheckout = (lookupKey, originUrl) =>
  billingApi
    .post("/billing/checkout", { lookup_key: lookupKey, origin_url: originUrl })
    .then((r) => r.data);
export const pollCheckoutStatus = (sessionId) =>
  billingApi.get(`/billing/status/${sessionId}`).then((r) => r.data);
export const openCustomerPortal = () =>
  billingApi.post("/billing/portal", {}).then((r) => r.data);
